import { Component, OnDestroy, AfterViewInit, OnInit } from '@angular/core';
import { TERMS_VERSION, PRIVACY_URL } from '../../legal/terms';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { AmplifyAuthenticatorModule, AuthenticatorService } from '@aws-amplify/ui-angular';
import { updateUserAttributes, signUp, SignUpInput, SignUpOutput, fetchAuthSession } from 'aws-amplify/auth';
import { Router, ActivatedRoute } from '@angular/router';
import { DialogModule } from 'primeng/dialog';
import { ButtonModule } from 'primeng/button';
import { CheckboxModule } from 'primeng/checkbox';
import { RecaptchaService } from '../../services/recaptcha.service';
import { CompaniesService } from '../../services/companies.service';
import { SetupProgressService } from '../../services/setup-progress.service';
import { MarketingContext, OnboardingTrackingService } from '../../services/onboarding-tracking.service';
import { PostHogService } from '../../services/posthog.service';
import { FunnelAnalyticsService, normalizeError } from '../../services/funnel-analytics.service';
// Kept as a separate import so the original aws-amplify/auth line stays untouched
// (the /signup deep-link commit edits the adjacent Router import).
import { confirmSignUp, ConfirmSignUpInput, ConfirmSignUpOutput, signIn, SignInInput, SignInOutput } from 'aws-amplify/auth';

/**
 * Identifies the user who most recently authenticated in this browser, so a repeat
 * login by the SAME user can reuse the company/attribute caches instead of paying
 * for a cold refetch on every sign-in.
 */
const LAST_AUTHENTICATED_USER_KEY = 'ag_last_authenticated_user';

@Component({
  selector: 'app-login',
  standalone: true,
  imports: [CommonModule, FormsModule, AmplifyAuthenticatorModule, DialogModule, ButtonModule, CheckboxModule],
  templateUrl: './login.component.html',
  styleUrl: './login.component.scss'
})
export class LoginComponent implements OnDestroy, AfterViewInit, OnInit {

  private authSubscription: any;
  private mutationObserver?: MutationObserver;
  private pollingInterval?: any;
  /**
   * Re-entrancy guard. `AuthenticatorService.subscribe` emits on EVERY state-machine
   * transition, not once per sign-in, so the authenticated branch can fire several
   * times for a single login. Without this, the whole post-login sequence (cache
   * reset, reCAPTCHA, Cognito write, navigate) repeats — and each repeat re-runs the
   * route guards and their API calls. Prod logs show the resulting duplicate
   * concurrent company lookups.
   */
  private hasHandledAuthentication = false;
  /** The terms gate listens on mousedown AND click; one blocked submit must emit one event. */
  private lastTermsBlockAt = 0;
  showTermsModal = false;
  termsAccepted = false;
  /** Shared with the invitation flow; bump in src/app/legal/terms.ts when the text changes. */
  readonly termsVersion = TERMS_VERSION;
  readonly privacyUrl = PRIVACY_URL;
  /** ISO timestamp of the moment the user ticked the acceptance box. */
  private termsAcceptedAt: string | null = null;
  showTermsSection = false;
  showMarketingContent = false;
  // Drives the Amplify authenticator's opening tab. The /signup route sets 'signUp'.
  initialAuthState: 'signIn' | 'signUp' = 'signIn';
  
  formFields = {
    signIn: {
      username: {
        label: 'Email',
        placeholder: 'Enter your email'
      }
    },
    signUp: {
      email: {
        label: 'Email',
        placeholder: 'Enter your email',
        required: true,
        order: 1
      },
      given_name: {
        label: 'First Name',
        placeholder: 'Enter your first name',
        required: true,
        order: 2
      },
      family_name: {
        label: 'Last Name',
        placeholder: 'Enter your last name',
        required: true,
        order: 3
      },
      password: {
        label: 'Password',
        placeholder: 'Create a password',
        required: true,
        order: 4
      },
      confirm_password: {
        label: 'Confirm Password',
        placeholder: 'Confirm your password',
        required: true,
        order: 5
      }
    }
  };

  /**
   * CRITICAL: DO NOT MODIFY THIS SIGN-UP HANDLER
   * =============================================
   * This handler uses STANDARD Cognito attributes (given_name, family_name).
   * The Cognito User Pool is configured to accept these standard attributes.
   *
   * DO NOT map to custom attributes like 'name.givenName' or 'name.familyName'.
   * DO NOT change the attribute structure passed to signUp().
   *
   * If sign-up breaks, check:
   * 1. Cognito User Pool configuration matches these attributes
   * 2. formFields above matches what Cognito expects
   *
   * Last verified working: 2026-01-14
   * =============================================
   */
  services = {
    handleSignUp: async (input: SignUpInput): Promise<SignUpOutput> => {
      const { username, password, options } = input;

      console.log('🔐 Sign-up with standard Cognito attributes:', options?.userAttributes);
      this.funnel.track('signup', 'signup_started');

      // Refuse rather than record acceptance nobody gave. The submit button is also
      // disabled until the box is ticked, but that is a DOM-level guard; this is the
      // one that decides what reaches the audit trail. Never substitute a timestamp.
      if (!this.termsAccepted || !this.termsAcceptedAt) {
        this.funnel.trackFailure('signup', 'signup_failed', { name: 'TERMS_NOT_ACCEPTED', message: 'Terms not accepted' });
        throw new Error('Please accept the Terms of Service to create your account.');
      }

      try {
        // IMPORTANT: Pass through standard Cognito attributes without modification
        // The Cognito User Pool expects: given_name, family_name (NOT custom attributes)
        //
        // clientMetadata carries the Terms acceptance to the cognitoPreSignUp
        // trigger, which writes it to the acceptance audit table before the
        // account exists. It is metadata, not a user attribute, so it does not
        // trip that trigger's custom:-attribute guard.
        return await signUp({
          username,
          password,
          options: {
            ...options,
            // AuthSignUpOptions requires userAttributes to be present; spreading a
            // possibly-undefined options object would widen it to optional.
            userAttributes: options?.userAttributes ?? {},
            clientMetadata: {
              ...(options?.clientMetadata ?? {}),
              termsVersion: this.termsVersion,
              termsAcceptedAt: this.termsAcceptedAt
            }
          }
        });
      } catch (error) {
        // Observe only — Amplify still renders its own error message.
        this.funnel.trackFailure('signup', 'signup_failed', error);
        throw error;
      }
    },
    handleConfirmSignUp: async (input: ConfirmSignUpInput): Promise<ConfirmSignUpOutput> => {
      this.funnel.track('signup', 'signup_confirm_submitted');
      try {
        return await confirmSignUp(input);
      } catch (error) {
        this.funnel.trackFailure('signup', 'signup_confirm_failed', error);
        throw error;
      }
    },
    handleSignIn: async (input: SignInInput): Promise<SignInOutput> => {
      try {
        return await signIn(input);
      } catch (error) {
        this.funnel.trackFailure('login', 'login_failed', error);
        throw error;
      }
    }
  };

  // Terms of Service shown at account creation. Generated from the marketing site's
  // legal/terms-of-service.txt (npm run terms:generate there); paste the same text here
  // and bump TERMS_VERSION in src/app/legal/terms.ts whenever it changes.
  termsAndConditions = `
Terms of Service
Last Updated: September 14, 2026 (revision 2)

These Terms of Service ("Terms") govern your access to and use of the Anchored Growth platform, websites, APIs, and related services (the "Service"), provided by Factor15 Consulting, doing business as Anchored Growth ("Anchored Growth," "we," "us"). By creating an account, clicking accept, or using the Service, you agree to these Terms on behalf of yourself and the organization you represent ("Customer," "you"). If you do not agree, do not use the Service.

1. What the Service Does
Anchored Growth analyzes sales outreach data and returns recommendations. The Service ingests campaign and messaging data from the platforms you connect, applies statistical models to it, and produces estimates, scores, rankings, and suggested actions: when to send, which channel to use, what tone performs, and when a model has drifted.

What the Service does not do:
• We do not verify that Customer Data (defined in Section 3) is accurate, complete, current, or lawfully obtained. We analyze the data we receive.
• We do not decide who you contact or what you say to them. You do.
• We do not provide legal, compliance, tax, financial, employment, or other professional advice. Service outputs are not a substitute for advice from a qualified professional or for your own review.
• Where the Service performs an action you configure or request, such as starting a campaign in your sequencer or sending a reply you write or approve in the inbox, it acts on your instructions. Those are your communications, not ours.

2. Accounts
You must provide accurate registration information and keep your credentials confidential. You are responsible for all activity under your account, including by people you invite. Each person you invite must accept these Terms before using the Service, and we record the version each person accepts and when. Accounts are for business use by persons 18 or older. We may suspend accounts that violate these Terms or create risk for the Service or other customers.

3. Customer Data
"Customer Data" means data you submit to the Service or authorize us to pull from connected platforms, including prospect names, contact details, message content, and campaign outcomes.
• You own Customer Data. You grant us a non-exclusive, worldwide license to host, process, transmit, analyze, and display Customer Data as needed to provide and secure the Service.
• You represent that you have all rights, consents, and lawful bases required to collect the Customer Data and to authorize our processing of it, including any personal data of prospects and clients.
• Between the parties, you are the controller and business for personal data in Customer Data, and we are your processor and service provider. You are responsible for privacy notices, lawful bases, consents, opt-outs, retention decisions, and for responding to requests from the people whose data you send us. We will give you reasonable help with those requests at your expense.
• We store message content and outcomes because the models require history to produce estimates. We keep it while your account is active, as described in our Privacy Policy.
• You authorize us to engage subprocessors to provide the Service, including cloud hosting, email delivery, payment processing, product analytics, and model providers. We remain responsible for their performance under these Terms. Our Privacy Policy at anchoredgrowth.io/privacy lists our current subprocessors and what each one processes, and we update that list before a new subprocessor begins processing Customer Data. We also email you at least 14 days before that happens. If you object on reasonable data-protection grounds and we cannot address the objection, you may terminate the affected part of the Service.
• Where required by applicable data protection law, the parties will execute a data processing addendum, which governs in the event of a conflict with these Terms.
• If we become aware of a breach of security leading to the unlawful destruction, loss, alteration, or disclosure of Customer Data, we will notify you without undue delay and give you the information you reasonably need to meet your own notification obligations.
• Aggregated or de-identified data that does not identify you, your prospects, or your clients may be used to improve our models and the Service.
• On termination, you may export Customer Data for 30 days. After that we may delete it, subject to routine backups.

4. Your Responsibilities and Acceptable Use
You, not Anchored Growth, are responsible for the outreach you send. You agree to:
• Comply with all laws that apply to your outreach and data, including CAN-SPAM, CASL, GDPR, the UK GDPR, CCPA, the TCPA, and the terms of every platform you connect, including LinkedIn, email providers, and CRMs.
• Obtain any consents required to contact prospects and to share their data with us.
• Review Service outputs before acting on them, and configure any automation you enable.
• Not use the Service to send unlawful, deceptive, or harassing communications, to scrape or contact people in violation of a platform's rules, or to process data of children.
• Not reverse engineer the Service, probe or breach its security, resell it without authorization, or use it to build a competing product.
We may suspend the Service without notice if your use creates legal exposure, security risk, or deliverability harm for us or other customers.

5. Recommendations Are Estimates, Not Guarantees
Every output of the Service is a statistical estimate produced from the data available to it. Estimates carry uncertainty. They can be wrong, and they will be wrong more often when the underlying data is sparse, stale, biased, or inaccurate.

You accept that:
• Reply-rate predictions, send-time recommendations, tone analysis, position and channel scores, drift alerts, and generated message drafts may be incorrect, incomplete, or unsuitable for your situation.
• We do not warrant any level of campaign performance, reply rate, revenue, deliverability, sender reputation, or model accuracy.
• You are solely responsible for every decision you make and every action you take or automate on the basis of Service outputs, and for reviewing outputs before they reach a recipient.
• We are not liable for losses arising from your reliance on Service outputs, from outputs that are wrong or incomplete, or from outputs derived from Customer Data that was inaccurate or incomplete when we received it.
• Statistical guardrails such as significance thresholds, confidence labels, and sample-size warnings reduce error. They do not eliminate it. A result labeled confident can still be wrong.

6. Fees, Trials, and Renewal
• Paid plans bill in advance on a recurring basis at the prices shown at purchase. Taxes are your responsibility.
• Free trials require a valid payment card. When a trial ends, the subscription converts to a paid plan at the price shown to you when you started the trial, unless you cancel first. The plan and price are stated on the trial card at signup.
• Subscriptions renew until canceled. Cancellation takes effect at the end of the current billing period. Fees already paid are non-refundable except where the law requires otherwise.
• We may change prices with at least 30 days' notice. Changes apply at your next renewal.

7. Confidentiality and Security
Each party will protect the other's non-public information with reasonable care and use it only to perform under these Terms. We maintain administrative, technical, and physical safeguards designed to protect Customer Data, and we back up Customer Data on a recurring schedule. Backups lower the chance of losing data but cannot rule it out, and no system is perfectly secure. Keep your own copy of any data you cannot afford to lose. You are responsible for securing your credentials and your connected accounts.

8. Intellectual Property
We own the Service, our models, and all related intellectual property. Feedback you provide may be used without restriction or obligation. These Terms grant no rights except those stated.

9. Third-Party Platforms
The Service depends on the platforms you connect. We do not control them and are not responsible for their availability or their acts. A platform may change its API or terms, throttle your account, or revoke access, which may reduce or end Service functionality without liability on our part. A platform may also restrict or close your account because of outreach you sent. That risk is yours.

10. Disclaimers
THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE." WE DISCLAIM ALL WARRANTIES, EXPRESS OR IMPLIED, INCLUDING MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, AND NON-INFRINGEMENT. WE DO NOT WARRANT THAT THE SERVICE WILL BE UNINTERRUPTED OR ERROR-FREE, THAT ANY OUTPUT WILL BE ACCURATE OR SUITABLE FOR YOUR PURPOSE, OR THAT ANY BUSINESS RESULT WILL FOLLOW FROM USING IT.

11. Limitation of Liability
TO THE MAXIMUM EXTENT PERMITTED BY LAW: (a) NEITHER PARTY IS LIABLE FOR INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, OR FOR LOST PROFITS, REVENUE, BUSINESS, GOODWILL, OR DATA, INCLUDING LOSS OF CUSTOMER DATA DESPITE THE BACKUPS DESCRIBED IN SECTION 7; (b) WE ARE NOT LIABLE FOR CLAIMS ARISING FROM YOUR RELIANCE ON SERVICE OUTPUTS, FROM COMMUNICATIONS YOU SENT OR AUTOMATED, OR FROM THE ACCURACY OR LEGALITY OF CUSTOMER DATA; AND (c) OUR TOTAL LIABILITY UNDER THESE TERMS IS CAPPED AT THE GREATER OF THE FEES YOU PAID US IN THE 12 MONTHS BEFORE THE CLAIM OR ONE HUNDRED U.S. DOLLARS. THESE LIMITS DO NOT APPLY TO YOUR PAYMENT OR INDEMNIFICATION OBLIGATIONS, OR TO LIABILITY THAT CANNOT BE LIMITED BY LAW.

12. Indemnification
You will defend and indemnify Anchored Growth against third-party claims arising from Customer Data, communications you send or automate, your violation of law or platform terms, or your breach of Section 4, except to the extent a claim is caused by our breach of these Terms or our negligence. We will defend and indemnify you against third-party claims that the Service, as provided by us and used as permitted, infringes their intellectual property. The party seeking indemnity must give prompt written notice of the claim and reasonable cooperation. A delay in notice relieves the indemnifying party only to the extent the delay prejudices its defense. The indemnifying party controls the defense and any settlement, and no settlement may impose an obligation on the other party without its consent.

13. Termination
Either party may terminate for material breach uncured 30 days after written notice. We may terminate immediately for violations of Section 4. Sections 1, 4, 5, 7, 8, and 9 through 15 survive termination. So do the parts of Section 3 covering the export window, data-protection roles, and aggregated or de-identified data, and your obligation to pay fees accrued before termination.

14. Changes to These Terms
We may update these Terms. For material changes we will give notice by email or in the Service at least 30 days before they take effect. If you do not agree to a material change, you may cancel before it takes effect. Continued use after the effective date is acceptance.

15. General
These Terms are governed by the laws of the State of Ohio, excluding its conflict-of-law rules. The state and federal courts located in Ohio have exclusive jurisdiction over any dispute arising from these Terms, and each party consents to that jurisdiction. These Terms plus any order form are the entire agreement and supersede prior discussions, including any prior beta testing terms. If a provision is unenforceable, the rest stand. Neither party may assign these Terms without consent, except to a successor in a merger or asset sale. Written notice under these Terms includes email: to you at your account's email address, and to us at contact@anchoredgrowth.io.

Questions and legal notices: contact@anchoredgrowth.io

By creating an account, you acknowledge that you have read, understood, and agree to be bound by these Terms.
  `.trim();

  passwordPolicy = {
    minLength: 8,
    requireNumbers: true,
    requireSpecialChars: true,
    requireUppercase: true,
    requireLowercase: true,
    requirements: [
      { text: 'Password minimum length 8 character(s)', met: false, regex: /.{8,}/ },
      { text: 'Contains at least 1 number', met: false, regex: /\d/ },
      { text: 'Contains at least 1 special character', met: false, regex: /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/ },
      { text: 'Contains at least 1 uppercase letter', met: false, regex: /[A-Z]/ },
      { text: 'Contains at least 1 lowercase letter', met: false, regex: /[a-z]/ }
    ]
  };

  validatePassword(password: string): void {
    this.passwordPolicy.requirements.forEach(requirement => {
      requirement.met = requirement.regex.test(password);
    });
  }

  showTerms(): void {
    this.showTermsModal = true;
  }

  hideTerms(): void {
    this.showTermsModal = false;
  }

  onTermsAcceptanceChange(accepted: boolean): void {
    this.termsAccepted = accepted;
    this.termsAcceptedAt = accepted ? new Date().toISOString() : null;
    // Force immediate button state update
    setTimeout(() => {
      this.updateSignUpButtonState();
    }, 10);
  }

  ngOnInit(): void {
    // Initialize terms acceptance state
    this.termsAccepted = false;
    this.showTermsSection = false;

    // The /signup route (data.signup) or ?signup=1 opens the Create Account tab.
    if (this.routedToSignup()) {
      this.initialAuthState = 'signUp';
    }

    // Detect if user came from external source
    this.detectReferrerSource();

    // Signup arrival: record the funnel entry and pin the marketing context as
    // super properties so every later event carries it.
    if (this.routedToSignup()) {
      const marketing = this.trackingService.trackSignupPageViewed();
      this.posthog.registerOnce(this.marketingSuperProperties(marketing));
    }
  }

  /**
   * Signup mode = the /signup route (data.signup) or a ?signup=1 query param —
   * the same rule the /signup deep-link commit uses to open the Create Account tab.
   */
  private routedToSignup(): boolean {
    return this.activatedRoute.snapshot.data['signup'] === true
      || this.activatedRoute.snapshot.queryParamMap.get('signup') !== null;
  }

  /** Map the legacy marketing context onto PostHog super-property names, non-empty values only. */
  private marketingSuperProperties(context: MarketingContext): Record<string, string> {
    const candidates: Record<string, string | undefined> = {
      utm_source: context.utmSource,
      utm_medium: context.utmMedium,
      utm_campaign: context.utmCampaign,
      referrer: context.referrer,
      selected_tier: context.selectedTier
    };
    const properties: Record<string, string> = {};
    for (const [key, value] of Object.entries(candidates)) {
      if (value) properties[key] = value;
    }
    return properties;
  }

  /**
   * Detect if the user came from an external source
   * Show marketing content if they didn't come from anchoredgrowth.io
   */
  private detectReferrerSource(): void {
    // Check if we've already determined this in the current session
    const storedValue = sessionStorage.getItem('showMarketingContent');

    if (storedValue !== null) {
      this.showMarketingContent = storedValue === 'true';
      return;
    }

    // Get the referrer (the page that linked to this page)
    const referrer = document.referrer;

    // Show marketing if:
    // 1. No referrer (direct visit, bookmark, typed URL)
    // 2. Referrer doesn't contain our domain
    const isExternalVisitor = !referrer || !referrer.includes('anchoredgrowth.io');

    this.showMarketingContent = isExternalVisitor;

    // Store the decision for the session to avoid flickering on page reload
    sessionStorage.setItem('showMarketingContent', String(this.showMarketingContent));

    console.log('🎯 Referrer detection:', {
      referrer: referrer || 'none',
      showMarketing: this.showMarketingContent
    });
  }

  /**
   * Switch to the sign-up tab when user clicks "Start Free Trial" CTA
   */
  switchToSignUp(): void {
    console.log('🚀 switchToSignUp() called');

    // Find and click the sign-up tab (Create Account)
    setTimeout(() => {
      const tabs = document.querySelectorAll('[data-amplify-authenticator] .amplify-tabs__item');
      console.log('🔍 Found tabs:', tabs.length);

      for (const tab of Array.from(tabs)) {
        const tabText = tab.textContent?.toLowerCase() || '';
        if (tabText.includes('create') || tabText.includes('sign up')) {
          console.log('✅ Found Create Account tab, triggering click...');
          const tabElement = tab as HTMLElement;

          // Try multiple click methods
          tabElement.click();

          // Also dispatch mouse events for frameworks that need them
          tabElement.dispatchEvent(new MouseEvent('mousedown', { bubbles: true, cancelable: true }));
          tabElement.dispatchEvent(new MouseEvent('mouseup', { bubbles: true, cancelable: true }));
          tabElement.dispatchEvent(new MouseEvent('click', { bubbles: true, cancelable: true }));

          // Focus the element
          tabElement.focus();

          console.log('✅ Click events dispatched');
          return;
        }
      }

      // Fallback: click second tab
      if (tabs.length > 1) {
        console.log('⚠️ Using fallback - clicking second tab');
        (tabs[1] as HTMLElement).click();
      }
    }, 100);
  }

  ngAfterViewInit(): void {
    // Set up a mutation observer to watch for DOM changes in the authenticator
    this.setupFormValidation();
  }

  private setupFormValidation(): void {
    // Use a timeout to ensure the authenticator has rendered
    setTimeout(() => {
      this.observeAuthenticatorChanges();
      this.updateAuthenticatorState();
      // Also set up click listeners on tabs
      this.setupTabClickListeners();
      // Start polling to catch state changes
      this.startPolling();
    }, 100);
  }

  private startPolling(): void {
    // Poll every 250ms to check for state changes and update button state
    this.pollingInterval = setInterval(() => {
      this.updateTermsVisibility();
      this.updateSignUpButtonState();
    }, 250);
  }

  private setupTabClickListeners(): void {
    // Set up click listeners on the authenticator tabs
    setTimeout(() => {
      const signInTab = document.querySelector('[data-amplify-authenticator] [data-amplify-router-content="signIn"]');
      const signUpTab = document.querySelector('[data-amplify-authenticator] [data-amplify-router-content="signUp"]');
      
      if (signInTab) {
        signInTab.addEventListener('click', () => {
          setTimeout(() => this.updateAuthenticatorState(), 100);
        });
      }
      
      if (signUpTab) {
        signUpTab.addEventListener('click', () => {
          setTimeout(() => this.updateAuthenticatorState(), 100);
        });
      }

      // Also try alternative selectors for tabs
      const tabs = document.querySelectorAll('[data-amplify-authenticator] .amplify-tabs__item, [data-amplify-authenticator] [role="tab"]');
      tabs.forEach(tab => {
        tab.addEventListener('click', () => {
          setTimeout(() => this.updateAuthenticatorState(), 100);
        });
      });
    }, 200);
  }

  private observeAuthenticatorChanges(): void {
    const authenticatorElement = document.querySelector('[data-amplify-authenticator]');
    if (authenticatorElement) {
      this.mutationObserver = new MutationObserver(() => {
        this.updateAuthenticatorState();
      });
      
      this.mutationObserver.observe(authenticatorElement, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['data-amplify-router-state']
      });
    }
  }

  private updateAuthenticatorState(): void {
    this.updateTermsVisibility();
    this.updateSignUpButtonState();
  }

  private updateTermsVisibility(): void {
    setTimeout(() => {
      // Try multiple ways to detect sign up view
      const isSignUpView = document.querySelector('[data-amplify-authenticator][data-amplify-router-state="signUp"]') ||
                          document.querySelector('[data-amplify-authenticator] [data-amplify-router-content="signUp"]') ||
                          document.querySelector('[data-amplify-authenticator] .amplify-tabs__item--active[aria-label*="Sign up"]') ||
                          document.querySelector('[data-amplify-authenticator] .amplify-tabs__item--active')?.textContent?.toLowerCase().includes('sign up') ||
                          document.querySelector('[data-amplify-authenticator] .amplify-tabs__item--active')?.textContent?.toLowerCase().includes('create');

      const wasVisible = this.showTermsSection;
      this.showTermsSection = !!isSignUpView;
      
      // Debug logging
      // Reset terms acceptance when switching views
      if (!this.showTermsSection && wasVisible) {
        this.termsAccepted = false;
        this.termsAcceptedAt = null;
      }
    }, 50);
  }

  private updateSignUpButtonState(): void {
    // Use a small delay to ensure DOM has updated
    setTimeout(() => {
      // Try multiple selectors to find the signup button
      const signUpButton = (
        document.querySelector('[data-amplify-authenticator][data-amplify-router-state="signUp"] button[type="submit"]') ||
        document.querySelector('[data-amplify-authenticator] form button[type="submit"]') ||
        document.querySelector('[data-amplify-authenticator] button[data-amplify-button-type="submit"]') ||
        document.querySelector('amplify-authenticator button[type="submit"]')
      ) as HTMLButtonElement;
      
   
      
      if (signUpButton && this.showTermsSection) {
        if (!this.termsAccepted) {
          signUpButton.disabled = true;
          signUpButton.style.opacity = '0.5';
          signUpButton.style.cursor = 'not-allowed';
          signUpButton.title = 'Please accept the Terms of Service to continue';
          
          // Add click event listener to prevent form submission
          this.addFormSubmissionPrevention(signUpButton);
        } else {
          signUpButton.disabled = false;
          signUpButton.style.opacity = '1';
          signUpButton.style.cursor = 'pointer';
          signUpButton.title = '';
          
          // Remove the prevention if terms are accepted
          this.removeFormSubmissionPrevention(signUpButton);
        }
      }
    }, 50);
  }

  private addFormSubmissionPrevention(button: HTMLButtonElement): void {
    // Remove any existing listeners first
    this.removeFormSubmissionPrevention(button);
    
    // Create the prevention handler
    const preventSubmission = (event: Event) => {
      if (!this.termsAccepted) {
        console.warn('Sign-up prevented: Terms and conditions must be accepted');
        const now = Date.now();
        if (now - this.lastTermsBlockAt > 500) {
          this.lastTermsBlockAt = now;
          this.funnel.trackFailure('signup', 'signup_failed', { name: 'TERMS_NOT_ACCEPTED', message: 'Terms not accepted' });
        }
        event.preventDefault();
        event.stopPropagation();
        event.stopImmediatePropagation();
        
        // Show user-friendly message
        alert('Please accept the Terms of Service to continue with account creation.');
        
        return false;
      }
      console.log('Sign-up allowed: Terms accepted');
      return true;
    };
    
    // Store reference for removal later
    (button as any)._termsPreventionHandler = preventSubmission;
    
    // Add listeners for multiple events to ensure we catch the submission
    button.addEventListener('click', preventSubmission, true);
    button.addEventListener('mousedown', preventSubmission, true);
    button.addEventListener('keydown', (event: KeyboardEvent) => {
      if (event.key === 'Enter' || event.key === ' ') {
        preventSubmission(event);
      }
    }, true);
    
    // Also add to the form if we can find it
    const form = button.closest('form');
    if (form) {
      form.addEventListener('submit', preventSubmission, true);
      (form as any)._termsPreventionHandler = preventSubmission;
    }

    // Add to the amplify authenticator element itself
    const authenticator = document.querySelector('amplify-authenticator');
    if (authenticator) {
      authenticator.addEventListener('submit', preventSubmission, true);
      (authenticator as any)._termsPreventionHandler = preventSubmission;
    }
  }

  private removeFormSubmissionPrevention(button: HTMLButtonElement): void {
    // Remove listeners from button
    if ((button as any)._termsPreventionHandler) {
      const handler = (button as any)._termsPreventionHandler;
      button.removeEventListener('click', handler, true);
      button.removeEventListener('mousedown', handler, true);
      button.removeEventListener('keydown', handler, true);
      delete (button as any)._termsPreventionHandler;
    }
    
    // Remove form listener
    const form = button.closest('form');
    if (form && (form as any)._termsPreventionHandler) {
      form.removeEventListener('submit', (form as any)._termsPreventionHandler, true);
      delete (form as any)._termsPreventionHandler;
    }

    // Remove authenticator listener
    const authenticator = document.querySelector('amplify-authenticator');
    if (authenticator && (authenticator as any)._termsPreventionHandler) {
      authenticator.removeEventListener('submit', (authenticator as any)._termsPreventionHandler, true);
      delete (authenticator as any)._termsPreventionHandler;
    }
  }

  constructor(
    private router: Router,
    private activatedRoute: ActivatedRoute,
    public authenticator: AuthenticatorService,
    private recaptchaService: RecaptchaService,
    private companiesService: CompaniesService,
    private setupProgressService: SetupProgressService,
    private trackingService: OnboardingTrackingService,
    private posthog: PostHogService,
    private funnel: FunnelAnalyticsService
  ) {
    this.authSubscription = this.authenticator.subscribe((data: any) => {
      if (data.authStatus !== 'authenticated') {
        // A subsequent sign-in on this same component instance must be handled again.
        this.hasHandledAuthentication = false;
        return;
      }

      if (this.hasHandledAuthentication) {
        return;
      }
      this.hasHandledAuthentication = true;

      void this.handleAuthenticated();
    });
  }

  /**
   * Post-authentication handoff to the app.
   *
   * Ordering here is what the user experiences as "login speed". Only work that must
   * complete BEFORE the route guards run is awaited; everything else is fired without
   * blocking the redirect. Previously reCAPTCHA (which carries its own 10s internal
   * timeout) and a Cognito attribute write both sat in front of `router.navigate()`,
   * so a slow Google or Cognito round trip held the user on the login form for
   * seconds after they were already signed in.
   */
  private async handleAuthenticated(): Promise<void> {
    // Must precede navigation — the route guards read these caches immediately.
    const userId = await this.clearCachesIfUserChanged();

    // Identity first, so login_succeeded and everything after it joins to the person.
    if (userId) {
      this.posthog.identify({ userId });
    }
    this.funnel.track('login', 'login_succeeded');

    this.router.navigate(['/']);

    // Neither of these gates access to the app, so neither should delay it.
    // Housekeeping after a login that already succeeded: a WARNING event (no
    // *_failed suffix, no $exception) so it never trips the funnel failure alert.
    this.recaptchaService.verifyAndStore('login')
      .catch(error => {
        console.error('reCAPTCHA verification failed:', error);
        this.funnel.track('login', 'login_post_auth_warning', { what: 'recaptcha', ...normalizeError(error) });
      });
    this.updateLastLogin()
      .catch(error => {
        console.error('Failed to update last login:', error);
        this.funnel.track('login', 'login_post_auth_warning', { what: 'last_login', ...normalizeError(error) });
      });
  }

  /**
   * Drop cached company/user state only when a DIFFERENT user signs in.
   *
   * This replaces an unconditional reset that ran on every login and guaranteed a
   * cold refetch of companies + Cognito attributes every single time. The property
   * that reset existed to protect — user A's cached data must never be visible to
   * user B on a shared browser — is preserved by keying on the authenticated user id.
   *
   * A normal sign-out already wipes everything via `SessionCleanupService`; this
   * covers the paths that skip it, e.g. a session that simply expired and a
   * different person then signing in.
   */
  private async clearCachesIfUserChanged(): Promise<string | undefined> {
    try {
      const session = await fetchAuthSession();
      const userId = session.tokens?.idToken?.payload?.sub as string | undefined;

      if (!userId) {
        throw new Error('No subject claim on the authenticated session');
      }

      if (userId !== localStorage.getItem(LAST_AUTHENTICATED_USER_KEY)) {
        this.resetUserScopedState();
      }

      localStorage.setItem(LAST_AUTHENTICATED_USER_KEY, userId);
      return userId;
    } catch (error) {
      // If we cannot establish who just signed in, fail SAFE and wipe the caches.
      console.error('Could not resolve the authenticated user; clearing cached state:', error);
      this.resetUserScopedState();
      return undefined;
    }
  }

  /**
   * Clears every cache the route guards read. Setup progress is included because
   * `OnboardingGuard` decides whether to divert to `/setup` from it — leaving the
   * previous user's copy behind would let one user's setup state route another user.
   */
  private resetUserScopedState(): void {
    this.companiesService.resetSelectionState();
    this.setupProgressService.clearLocalCache();
  }

  private async updateLastLogin() {
    await updateUserAttributes({
      userAttributes: {
        'custom:lastLogin': new Date().toISOString()
      }
    });

    console.log('Last login timestamp updated');
  }

  ngOnDestroy() {
    if (this.authSubscription) {
      this.authSubscription.unsubscribe();
    }
    
    if (this.mutationObserver) {
      this.mutationObserver.disconnect();
    }
    
    if (this.pollingInterval) {
      clearInterval(this.pollingInterval);
    }
    
    // Clean up any remaining form submission prevention listeners
    const signUpButton = document.querySelector('[data-amplify-authenticator] button[type="submit"]') as HTMLButtonElement;
    if (signUpButton) {
      this.removeFormSubmissionPrevention(signUpButton);
    }
  }
}